import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCardClick = () => {
    navigate(`/product/${product?.id}`);
  };

  const handleAddToCart = (e) => {
    e?.stopPropagation();
    onAddToCart(product);
  };

  const getStockBadgeColor = () => {
    if (product?.stock > 50) return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: 'var(--color-success)' };
    if (product?.stock > 10) return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: 'var(--color-warning)' };
    return { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: 'var(--color-error)' };
  };

  const stockColors = getStockBadgeColor();

  return (
    <div
      className="group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
      style={{
        background: 'var(--color-card)',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 20px 40px rgba(14, 165, 233, 0.2)' : '0 4px 12px rgba(14, 165, 233, 0.1)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative w-full overflow-hidden" style={{ height: '240px' }}>
        {!imageLoaded && (
          <div className="absolute inset-0 animate-pulse"
            style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)' }} />
        )}
        
        {/* Blurred Vial Background Layer - 75% blur */}
        <div className="absolute inset-0">
          <Image
            src="/assets/images/injection-bottle-glass-vial-with-blank-white-label_53876-165420-1770528727675.jpg"
            alt=""
            className="w-full h-full object-cover blur-sm scale-110"
            onLoad={() => setImageLoaded(true)}
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        {/* INF Logo Overlay - Prominent */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20">
          <img
            src="/assets/images/Copy_of_Black_White_Modern_Handwritten_Square_Studio_Logo-removebg-preview-1770507171149.png"
            alt="INF LABS"
            className="w-32 h-32 md:w-40 md:h-40 object-contain opacity-50 drop-shadow-2xl"
          />
        </div>
        
        {/* Stock Badge */}
        <div
          className="absolute top-3 md:top-4 right-3 md:right-4 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold"
          style={{
            background: stockColors?.bg,
            border: `1px solid ${stockColors?.border}`,
            color: stockColors?.text
          }}
        >
          {product?.stock > 0 ? `${product?.stock} in stock` : 'Out of Stock'}
        </div>

        {/* New Badge */}
        {product?.isNew && (
          <div
            className="absolute top-3 md:top-4 left-3 md:left-4 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-bold"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
              color: 'var(--color-primary-foreground)',
              boxShadow: '0 0 12px rgba(0, 212, 255, 0.5)'
            }}
          >
            NEW
          </div>
        )}

        {/* Quick View Overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
          style={{
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(8px)',
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? 'auto' : 'none'
          }}
        >
          <div className="flex items-center gap-2 md:gap-3 text-primary text-sm md:text-base font-medium">
            <Icon name="Eye" size={20} />
            <span>Quick View</span>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="p-4 md:p-6 space-y-3 md:space-y-4">
        {/* Category */}
        <div className="flex items-center gap-2">
          <Icon name="Tag" size={14} color="var(--color-muted-foreground)" />
          <span className="text-xs md:text-sm text-muted-foreground">{product?.category}</span>
        </div>

        {/* Product Name */}
        <h3 className="text-base md:text-lg font-bold text-foreground line-clamp-2 min-h-[3rem] md:min-h-[3.5rem]">
          {product?.name}
        </h3>

        {/* Dosage & Brand */}
        <div className="flex items-center justify-between text-xs md:text-sm">
          <div className="flex items-center gap-1.5 md:gap-2 text-muted-foreground">
            <Icon name="Pill" size={14} />
            <span>{product?.dosage}</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 text-muted-foreground">
            <Icon name="Award" size={14} />
            <span className="line-clamp-1">{product?.brand}</span>
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[...Array(5)]?.map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={14}
                color="var(--color-accent)"
                className=""
              />
            ))}
          </div>
          <span className="text-xs md:text-sm text-muted-foreground">
            ({product?.reviews})
          </span>
        </div>

        {/* Price & Add to Cart */}
        <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-border">
          <div>
            <p className="text-xl md:text-2xl font-bold gradient-text">
              ${product?.price?.toFixed(2)}
            </p>
            {product?.originalPrice && (
              <p className="text-xs md:text-sm text-muted-foreground line-through">
                ${product?.originalPrice?.toFixed(2)}
              </p>
            )}
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="ShoppingCart"
            iconPosition="left"
            onClick={handleAddToCart}
            disabled={product?.stock === 0}
            className="whitespace-nowrap"
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;